import { useMemo } from 'react';
import { Col, Row, Card, Button } from 'react-bootstrap';
import { useQuery, useMutation, NetworkStatus } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { validate as uuidValidate } from 'uuid';

import LinkContainer from '../components/link_container';
import { renderOverlay, renderOffline, renderError } from '../components/render_helpers';
import ReactTable from '../components/react_table/react_table';
import { toastSuccess, toastWarning, toastError } from '../lib/toast_helpers';
import {
  productCategoryListPageQuery,
  productCategoryDelete as productCategoryDeleteMutation,
} from '../graphql/product_category_queries';
import { settingsSet } from '../store/settings_slice';
import * as updateFunctions from '../update_functions';

const ProductCategoryList = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const settingsMutating = useSelector((state) => state.settings.mutating);
  const settingsOnline = useSelector((state) => state.settings.online);
  const settingsTenant = useSelector((state) => state.settings.tenant);
  const [productCategoryDelete] = useMutation(productCategoryDeleteMutation);

  const {
    data: pageData,
    loading: pageLoading,
    error: pageError,
    refetch: pageRefetch,
    networkStatus: pageNetworkStatus,
    client: pageClient,
  } = useQuery(productCategoryListPageQuery, {
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ productCategoryList }) => {
      // write an individual query for every productCategory in the list
      updateFunctions.productCategoryList(pageClient, productCategoryList);
    },
  });

  const pageLoadedOrRefetching = useMemo(
    () => !pageLoading || (pageLoading && pageNetworkStatus === NetworkStatus.refetch),
    [pageLoading, pageNetworkStatus]
  );

  const productCategoryDeleteClicked = async (e) => {
    const dataId = e.currentTarget.getAttribute('data-id');
    const productCategoryId = uuidValidate(dataId)
      ? dataId
      : parseInt(e.currentTarget.getAttribute('data-id'), 10);
    const mutationData = {
      variables: { id: productCategoryId },
      context: {
        serializationKey: settingsTenant,
        tracked: true,
        recordType: 'ProductCategoryType',
        recordId: productCategoryId,
        mutationType: 'DELETE',
      },
      update: updateFunctions.productCategoryDelete,
    };
    mutationData.optimisticResponse = updateFunctions.optimistic(
      'productCategoryDelete',
      mutationData
    );
    dispatch(
      settingsSet({
        mutating: true,
      })
    );
    if (settingsOnline) {
      try {
        await productCategoryDelete(mutationData);
        toastSuccess('Product Category delete ok');
      } catch (err) {
        console.log(err.toString());
        toastError('Product Category delete failed');
      } finally {
        dispatch(
          settingsSet({
            mutating: false,
          })
        );
      }
    } else {
      productCategoryDelete(mutationData);
      toastWarning(`Product Category delete ok locally. Go online to make permanent`);
      dispatch(
        settingsSet({
          mutating: false,
        })
      );
    }
  };

  const parentColumns = [
    {
      header: 'Name',
      accessorKey: 'name',
      enableColumnFilter: true,
      filterFn: 'includesString',
    },
  ];

  const renderContent = () => (
    <>
      <Row className="mt-4 mb-3">
        <Col sm={12}>
          <div className="float-none">
            <div className="float-start">
              <h1 className="h3 mb-3">Product Categories</h1>
            </div>
            <div className="float-end">
              <Button
                className="me-2"
                variant="primary"
                onClick={() => pageRefetch()}
                disabled={!settingsOnline}
              >
                Refresh
              </Button>
              <LinkContainer to="/product_categories/new">
                <Button variant="primary">New Product Category</Button>
              </LinkContainer>
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <Card.Body>
              <ReactTable
                rootName="productCategory"
                parentColumns={parentColumns}
                data={pageData.productCategoryList}
                doShow={false}
                doDelete={currentUser?.perms?.administrator}
                handleDelete={productCategoryDeleteClicked}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  return (
    <div>
      {renderOverlay(pageLoading, settingsMutating, settingsOnline)}
      {renderOffline(settingsOnline)}
      {renderError(pageError)}
      {!pageError && pageLoadedOrRefetching && renderContent()}
    </div>
  );
};

export default ProductCategoryList;
